import React, { createContext, useState, useContext, ReactNode, useRef } from 'react';
import { TourStep } from '../components/GuidedTour';
import {tourSteps as steps} from './tourSteps';

interface TourContextType {
  activeTourStep: number;
  isTourActive: boolean;
  startTour: () => void;
  endTour: () => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
  steps: TourStep[];
  registerOnStart: (cb: () => void) => void;
  registerOnEnd: (cb: () => void) => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

interface TourProviderProps {
  children: ReactNode;
  onStart?: Array<() => void>;
  onEnd?: Array<() => void>;
}

let isTourActiveGlobal = false;
export function isTourActiveGlobalGetter() {
  return isTourActiveGlobal;
}

export const TourProvider: React.FC<TourProviderProps> = ({ 
  children,
  onStart,
  onEnd,
}) => {
  const [activeTourStep, setActiveTourStep] = useState<number>(-1);
  const [isTourActive, setIsTourActive] = useState<boolean>(false);

  // Store callbacks in refs to avoid re-registering on rerender
  const onStartCallbacks = useRef<(() => void)[]>([]);
  const onEndCallbacks = useRef<(() => void)[]>([]);

  const registerOnStart = (cb: () => void) => {
    onStartCallbacks.current.push(cb);
  };

  const registerOnEnd = (cb: () => void) => {
    onEndCallbacks.current.push(cb);
  };

  // Keep global flag in sync
  React.useEffect(() => {
    isTourActiveGlobal = isTourActive;
  }, [isTourActive]);

  // Register initial callbacks from props on mount
  React.useEffect(() => {
    if (onStart) {
      onStart.forEach(cb => registerOnStart(cb));
    }
    if (onEnd) {
      onEnd.forEach(cb => registerOnEnd(cb));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const startTour = () => {
    setActiveTourStep(0);
    setIsTourActive(true);
    onStartCallbacks.current.forEach(cb => cb());
  };

  const endTour = () => {
    setActiveTourStep(-1);
    setIsTourActive(false);
    onEndCallbacks.current.forEach(cb => cb());
  };

  const nextStep = () => {
    if (activeTourStep <= steps.length) {
      setActiveTourStep(prev => prev + 1);
    } else {
      endTour();
    }
  };

  const previousStep = () => {
    if (activeTourStep > 0) {
      setActiveTourStep(prev => prev - 1);
    }
  };

  const goToStep = (step: number) => {
    if (step >= 0 && step <= steps.length) {
      setActiveTourStep(step);
    }
  };

  return (
    <TourContext.Provider value={{
      activeTourStep,
      isTourActive,
      startTour,
      endTour,
      nextStep,
      previousStep,
      goToStep,
      steps,
      registerOnStart,
      registerOnEnd,
    }}>
      {children}
    </TourContext.Provider>
  );
};

export default TourContext;